import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const REMEMBER_KEY = "rietkuil_remember_me";
const SAVED_EMAIL_KEY = "rietkuil_saved_email";

export default function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState(() => localStorage.getItem(SAVED_EMAIL_KEY) ?? "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() => localStorage.getItem(REMEMBER_KEY) !== "false");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // If already signed in, go to admin
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate("/admin", { replace: true });
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) navigate("/admin", { replace: true });
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        localStorage.setItem(REMEMBER_KEY, remember ? "true" : "false");
        if (remember) {
          localStorage.setItem(SAVED_EMAIL_KEY, email);
        } else {
          localStorage.removeItem(SAVED_EMAIL_KEY);
        }
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/admin` },
        });
        if (error) throw error;
        setInfo("Account created. You can now sign in.");
        setMode("signin");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-0 py-3 border-b focus:outline-none text-sm font-sans font-light bg-transparent transition-colors placeholder:font-light";

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ backgroundColor: "#F0EBE0", color: "#3C3530" }}
    >
      <div
        className="w-full max-w-md rounded-lg p-10"
        style={{ backgroundColor: "#FDFAF4", border: "1px solid #E4DDD4" }}
      >
        <p
          className="text-base tracking-[0.1em] uppercase font-sans font-medium mb-4 text-center"
          style={{ color: "#4E4228" }}
        >
          Owner Access
        </p>
        <h1
          className="font-serif font-light text-3xl text-center mb-2"
          style={{ color: "#3C3530" }}
        >
          {mode === "signin" ? "Sign In" : "Create Account"}
        </h1>
        <div className="w-7 h-px mx-auto my-6" style={{ backgroundColor: "#A8B09E" }}></div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-sans font-medium uppercase tracking-[0.1em] mb-1" style={{ color: "#7E7265" }}>
              Email
            </label>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
              style={{ borderColor: "#C8C0B4", color: "#3C3530" }}
            />
          </div>
          <div>
            <label className="block text-sm font-sans font-medium uppercase tracking-[0.1em] mb-1" style={{ color: "#7E7265" }}>
              Password
            </label>
            <input
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
              style={{ borderColor: "#C8C0B4", color: "#3C3530" }}
            />
          </div>

          {mode === "signin" && (
            <label className="flex items-center gap-2 text-xs uppercase tracking-[0.1em] font-sans cursor-pointer select-none" style={{ color: "#7E7265" }}>
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-4 h-4 cursor-pointer"
                style={{ accentColor: "#4E4228" }}
              />
              Remember me
            </label>
          )}


          {error && (
            <p className="text-sm text-center font-sans font-light" style={{ color: "#9C7060" }}>
              {error}
            </p>
          )}
          {info && (
            <p className="text-sm text-center font-sans font-light" style={{ color: "#5E6A54" }}>
              {info}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full px-10 py-3.5 text-sm tracking-[0.1em] uppercase font-sans font-medium transition-all disabled:opacity-50 cursor-pointer rounded-full"
            style={{ backgroundColor: "#4E4228", color: "#F5F0E8" }}
          >
            {loading ? "Please wait..." : mode === "signin" ? "Sign In" : "Create Account"}
          </button>
        </form>

        <button
          onClick={() => {
            setMode(mode === "signin" ? "signup" : "signin");
            setError(null);
            setInfo(null);
          }}
          className="block w-full mt-6 text-xs uppercase tracking-[0.1em] font-sans cursor-pointer"
          style={{ color: "#7E7265" }}
        >
          {mode === "signin" ? "First time? Create owner account" : "Already have an account? Sign in"}
        </button>

        <Link
          to="/"
          className="block w-full mt-4 text-xs uppercase tracking-[0.1em] font-sans text-center"
          style={{ color: "#7E7265" }}
        >
          ← Back to website
        </Link>
      </div>

    </div>
  );
}
